import React, { useState } from 'react';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import Swal from 'sweetalert2';

import zovcurLogo from '../assets/logo/logo.jpg';
import { API_ENDPOINTS } from '../utils/api';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await axios.post(
        API_ENDPOINTS.login,
        { email, password },
        { validateStatus: (s) => s < 500 }
      );

      if (response.status !== 200) throw new Error(response.data?.error || 'Login failed');

      const token = response.data.token;
      localStorage.setItem('token', token);
      const decoded = jwtDecode(token);

      Swal.fire({
        icon: 'success',
        title: 'Welcome!',
        text: 'Login successful. Redirecting...',
        timer: 2000,
        showConfirmButton: false,
        timerProgressBar: true,
      });

      setTimeout(() => {
        if (decoded.role === 'master-admin') {
          navigate('/admin');
        } else if (decoded.role === 'admin') {
          navigate('/home');
        } else if (decoded.role) {
          navigate('/home');
        } else {
          navigate('/');
        }
      }, 2000);
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Login Failed',
        text: err?.message || 'Something went wrong.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-page min-h-screen flex flex-col lg:flex-row font-[Montserrat,sans-serif]">
      {/* Brand panel — top strip on mobile, side panel on desktop */}
      <aside className="login-brand relative flex flex-col justify-between overflow-hidden text-white lg:w-[48%] xl:w-[52%] px-6 sm:px-10 lg:px-12 xl:px-16 py-8 lg:py-12 min-h-[220px] lg:min-h-screen">
        <div className="login-brand__glow" aria-hidden="true" />
        <div className="login-brand__grid" aria-hidden="true" />

        <div className="relative z-10">
          <div className="inline-flex items-center rounded-2xl bg-white px-3 py-2 shadow-lg shadow-black/10">
            <img
              src={zovcurLogo}
              alt="Zovcur"
              className="h-9 sm:h-10 w-auto object-contain"
            />
          </div>
        </div>

        <div className="relative z-10 max-w-lg login-brand__copy mt-8 lg:mt-0">
          <p className="login-brand__eyebrow text-[11px] font-semibold tracking-[0.28em] uppercase mb-3 lg:mb-4">
            Zovcur Pharma
          </p>
          <h1 className="text-3xl sm:text-4xl xl:text-5xl font-bold leading-[1.15] tracking-tight mb-3 lg:mb-5">
            Work smarter.
            <br className="hidden sm:block" />
            {' '}Stay aligned.
          </h1>
          <p className="login-brand__sub text-sm sm:text-base xl:text-lg leading-relaxed font-medium max-w-md">
            Sign in to manage attendance, stock, invoices, and day-to-day operations
            in one place.
          </p>
        </div>

        <div className="relative z-10 hidden lg:block text-sm text-slate-300/70 font-medium">
          © {new Date().getFullYear()} Zovcur Pharma Pvt Ltd
        </div>
      </aside>

      {/* Form panel */}
      <main className="flex-1 flex flex-col justify-center px-6 sm:px-10 lg:px-14 xl:px-20 py-10 lg:py-12 bg-[#f4f7fa]">
        <div className="w-full max-w-[420px] mx-auto login-form-enter">
          <div className="mb-8">
            <h2 className="text-2xl sm:text-[1.75rem] font-bold text-[#0e3d52] tracking-tight">
              Sign in
            </h2>
            <p className="mt-2 text-sm text-slate-500 font-medium">
              Enter your credentials to continue to Worklogz
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label
                htmlFor="email"
                className="block text-[13px] font-semibold text-[#0e3d52] mb-1.5"
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="login-input w-full h-12 px-4 rounded-xl border border-slate-200 bg-white text-[#0e3d52] text-sm font-medium placeholder:text-slate-400 outline-none transition-shadow"
              />
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-[13px] font-semibold text-[#0e3d52] mb-1.5"
              >
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="login-input w-full h-12 px-4 pr-12 rounded-xl border border-slate-200 bg-white text-[#0e3d52] text-sm font-medium placeholder:text-slate-400 outline-none transition-shadow"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="login-icon-btn absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#0e8a9a] transition-colors p-1"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between gap-3 pt-0.5">
              <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300 text-[#0e8a9a] focus:ring-[#0e8a9a]/40"
                />
                <span className="text-sm font-medium text-slate-600">Remember me</span>
              </label>
              <button
                type="button"
                onClick={() =>
                  Swal.fire({
                    icon: 'info',
                    title: 'Forgot Password?',
                    text: 'Contact admin for password reset.',
                  })
                }
                className="login-link-btn text-sm font-semibold text-[#0e8a9a]"
              >
                Forgot password?
              </button>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="login-submit w-full h-12 mt-1 rounded-xl bg-[#0e3d52] text-white text-sm font-bold tracking-wide hover:bg-[#0a3142] disabled:opacity-60 disabled:cursor-not-allowed transition-all"
            >
              {isLoading ? (
                <span className="inline-flex items-center gap-2">
                  <span className="login-spinner" aria-hidden="true" />
                  Signing in…
                </span>
              ) : (
                'Sign in'
              )}
            </button>
          </form>

          <p className="mt-8 text-center text-sm text-slate-500 font-medium">
            Don&apos;t have an account?{' '}
            <a
              href="/register"
              className="font-bold text-[#0e8a9a] hover:text-[#0a6d7a] transition-colors"
            >
              Register
            </a>
          </p>

          <p className="mt-6 lg:hidden text-center text-xs text-slate-400 font-medium">
            © {new Date().getFullYear()} Zovcur Pharma Pvt Ltd
          </p>
        </div>
      </main>

      <style>{`
        .login-page {
          background: #f4f7fa;
        }
        .login-brand {
          background:
            radial-gradient(ellipse 80% 60% at 20% 10%, rgba(14, 138, 154, 0.45), transparent 55%),
            radial-gradient(ellipse 70% 50% at 90% 80%, rgba(232, 140, 60, 0.22), transparent 50%),
            linear-gradient(160deg, #072636 0%, #0e3d52 45%, #0a4a5c 100%);
          color: #ffffff;
        }
        .login-brand h1 {
          color: #ffffff !important;
        }
        .login-brand .login-brand__eyebrow {
          color: #99f6e4 !important;
        }
        .login-brand .login-brand__sub {
          color: rgba(226, 232, 240, 0.9) !important;
        }
        .login-page h2 {
          color: #0e3d52 !important;
          font-size: 1.75rem !important;
          font-family: Montserrat, 'Sora', sans-serif !important;
        }
        .login-page label {
          color: #0e3d52 !important;
        }
        .login-page a {
          color: #0e8a9a !important;
          text-decoration: none !important;
        }
        .login-page a:hover {
          color: #0a6d7a !important;
        }
        .login-brand__glow {
          position: absolute;
          inset: -20%;
          background: radial-gradient(circle at 40% 40%, rgba(255,255,255,0.06), transparent 45%);
          pointer-events: none;
        }
        .login-brand__grid {
          position: absolute;
          inset: 0;
          opacity: 0.07;
          background-image:
            linear-gradient(rgba(255,255,255,0.45) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.45) 1px, transparent 1px);
          background-size: 48px 48px;
          mask-image: linear-gradient(180deg, black, transparent 90%);
          pointer-events: none;
        }
        .login-brand__copy {
          animation: loginRise 0.7s ease-out both;
        }
        .login-form-enter {
          animation: loginRise 0.55s ease-out 0.08s both;
        }
        @keyframes loginRise {
          from { opacity: 0; transform: translateY(14px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .login-input {
          background: #ffffff !important;
          color: #0e3d52 !important;
          border: 1px solid #e2e8f0 !important;
          border-radius: 0.75rem !important;
          font-family: Montserrat, 'Poppins', sans-serif !important;
        }
        .login-input:focus {
          border-color: #0e8a9a !important;
          box-shadow: 0 0 0 3px rgba(14, 138, 154, 0.18) !important;
          outline: none !important;
        }
        .login-submit {
          background: #0e3d52 !important;
          color: #ffffff !important;
          border: none !important;
          border-radius: 0.75rem !important;
          font-family: Montserrat, 'Poppins', sans-serif !important;
          font-weight: 700 !important;
          padding: 0 !important;
        }
        .login-icon-btn,
        .login-link-btn {
          background: transparent !important;
          color: #64748b !important;
          border: none !important;
          padding: 0.25rem !important;
          box-shadow: none !important;
        }
        .login-link-btn {
          color: #0e8a9a !important;
        }
        .login-link-btn:hover {
          color: #0a6d7a !important;
          text-decoration: underline !important;
        }
        .login-icon-btn:hover {
          color: #0e8a9a !important;
        }
        .login-submit:not(:disabled):hover {
          background: #0a3142 !important;
          box-shadow: 0 10px 28px rgba(14, 61, 82, 0.28);
          transform: translateY(-1px);
        }
        .login-spinner {
          width: 14px;
          height: 14px;
          border: 2px solid rgba(255,255,255,0.35);
          border-top-color: #fff;
          border-radius: 50%;
          display: inline-block;
          animation: loginSpin 0.7s linear infinite;
        }
        @keyframes loginSpin {
          to { transform: rotate(360deg); }
        }
        @media (prefers-reduced-motion: reduce) {
          .login-brand__copy,
          .login-form-enter { animation: none; }
          .login-submit:not(:disabled):hover { transform: none; }
        }
      `}</style>
    </div>
  );
}

export default Login;
